/**
 * NotificationsBell -- Glocke mit Dropdown fuer In-System Notifications
 * ======================================================================
 * - Zeigt Badge mit Anzahl ungelesener Notifications
 * - Polled alle 60s
 * - Dropdown listet die letzten 20, klickbar zum Markieren-als-gelesen
 * - "Alle als gelesen" und Link zur Einstellungsseite
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, Check, Settings, Trash2, GraduationCap, UserPlus, AlertTriangle, FileText } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { NotificationItem, NotificationType } from '@/types'

const API = '/api/notifications'

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...opts.headers },
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

const TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
  new_grade: <GraduationCap size={16} className="text-blue-500" />,
  dualmatch_lead: <UserPlus size={16} className="text-fom-500" />,
  exam_failed_critical: <AlertTriangle size={16} className="text-amber-500" />,
  new_invoice: <FileText size={16} className="text-purple-500" />,
}

function timeAgo(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso.endsWith('Z') ? iso : iso + 'Z')
  const diff = Date.now() - date.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'gerade eben'
  if (mins < 60) return `vor ${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `vor ${hrs} h`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `vor ${days} d`
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const loadCount = useCallback(async () => {
    try {
      const res = await request<{ data: { count: number } }>('/unread-count')
      setUnread(res.data?.count ?? 0)
    } catch {
      // silent
    }
  }, [])

  const loadItems = useCallback(async () => {
    setLoading(true)
    try {
      const res = await request<{ data: NotificationItem[]; unread_count: number }>('?limit=20')
      setItems(res.data || [])
      setUnread(res.unread_count ?? 0)
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial + Polling
  useEffect(() => {
    loadCount()
    const id = setInterval(loadCount, 60000)
    return () => clearInterval(id)
  }, [loadCount])

  // Liste laden wenn geoeffnet
  useEffect(() => {
    if (open) loadItems()
  }, [open, loadItems])

  // Click-Outside zum Schliessen
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  async function handleMarkRead(id: number) {
    try {
      await request(`/${id}/read`, { method: 'POST' })
      setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
      setUnread(u => Math.max(0, u - 1))
    } catch {}
  }

  async function handleMarkAllRead() {
    try {
      await request('/read-all', { method: 'POST' })
      setItems(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnread(0)
    } catch {}
  }

  async function handleDelete(id: number, e: React.MouseEvent) {
    e.stopPropagation()
    try {
      await request(`/${id}`, { method: 'DELETE' })
      setItems(prev => prev.filter(n => n.id !== id))
      const removed = items.find(n => n.id === id)
      if (removed && !removed.is_read) setUnread(u => Math.max(0, u - 1))
    } catch {}
  }

  function handleClickItem(item: NotificationItem) {
    if (!item.is_read) handleMarkRead(item.id)
    if (item.link) {
      setOpen(false)
      if (item.link.startsWith('http')) {
        window.open(item.link, '_blank')
      } else {
        navigate(item.link)
      }
    }
  }

  function handleOpenSettings() {
    setOpen(false)
    navigate('/einstellungen/benachrichtigungen')
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative flex items-center justify-center w-10 h-10 rounded-lg text-white/90 hover:bg-white/15 hover:text-white transition-colors"
        title="Benachrichtigungen"
        type="button"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border border-white/20">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[380px] max-w-[calc(100vw-32px)] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div>
              <div className="text-sm font-bold text-gray-900">Benachrichtigungen</div>
              <div className="text-[11px] text-gray-500">
                {unread === 0 ? 'Alles gelesen' : `${unread} ungelesen`}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                  title="Alle als gelesen markieren"
                  type="button"
                >
                  <Check size={15} />
                </button>
              )}
              <button
                onClick={handleOpenSettings}
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                title="Einstellungen"
                type="button"
              >
                <Settings size={15} />
              </button>
            </div>
          </div>

          {/* Liste */}
          <div className="max-h-[480px] overflow-y-auto">
            {loading ? (
              <div className="py-12 text-center text-gray-400 text-sm">Laden...</div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={32} className="mx-auto text-gray-300 mb-2" />
                <div className="text-sm font-medium text-gray-500">Keine Benachrichtigungen</div>
                <div className="text-xs text-gray-400 mt-1">
                  Du wirst hier ueber wichtige Ereignisse informiert.
                </div>
              </div>
            ) : (
              items.map(item => (
                <div
                  key={item.id}
                  onClick={() => handleClickItem(item)}
                  className={`group flex items-start gap-3 px-4 py-3 border-b border-gray-50 cursor-pointer transition-colors ${
                    item.is_read ? 'bg-white hover:bg-gray-50' : 'bg-fom-50/40 hover:bg-fom-50'
                  }`}
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center mt-0.5">
                    {TYPE_ICONS[item.type] || <Bell size={16} className="text-gray-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`text-sm leading-tight ${item.is_read ? 'font-medium text-gray-700' : 'font-bold text-gray-900'}`}>
                        {item.title}
                      </div>
                      {!item.is_read && (
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-fom-500 mt-1.5" />
                      )}
                    </div>
                    {item.message && (
                      <div className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">
                        {item.message}
                      </div>
                    )}
                    <div className="text-[11px] text-gray-400 mt-1.5">
                      {timeAgo(item.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(item.id, e)}
                    className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Loeschen"
                    type="button"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <button
            onClick={handleOpenSettings}
            className="w-full px-4 py-3 text-sm font-semibold text-fom-600 hover:bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-2 transition-colors"
            type="button"
          >
            <Settings size={14} />
            Benachrichtigungen verwalten
          </button>
        </div>
      )}
    </div>
  )
}
